import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bell, X, CheckCircle, MessageSquare, Clock } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TableWithSearch from "@/components/TableWithSearch";
import { ColumnHeader } from "@/components/MyTable";
import {
  formatOrganizationTimezoneDisplay,
  formatDate,
  formatDateTime24,
  formatDateTime12,
  getPatientContextTimezoneDisplay,
  getPromise,
  getPromiseToast,
} from "@/utils/helpers";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { useNavigate } from "react-router";
import SnoozeClearTriageDialog from "@/components/SnoozeClearTriageDialog";
import { useAuth } from "@/contexts/use-auth";
import { useOrg } from "@/contexts/useOrg";
import { useGetData } from "@/hooks/useGetData";

const Alerts = () => {
  const navigate = useNavigate();
  const { user, hasModuleAdminAccess, profile: profileFromAuth, isLoading: authLoading } = useAuth();
  const { selectedOrgId, isAllSelected } = useOrg();
  const [profile, setProfile] = useState<any>(null);
  
  // Fetch profile if not available from auth context
  useEffect(() => {
    if (!authLoading && user) {
      if (profileFromAuth) {
        // Use profile from auth context if available
        setProfile(profileFromAuth);
      } else {
        // Fetch profile separately if not available in auth context
        const fetchProfile = async () => {
          try {
            const response = await AxiosInstance().get<any>("/auth/me");
            const responseData = response?.data as any;
            if (responseData?.data?.profile) {
              setProfile(responseData.data.profile);
            }
          } catch (error) {
            console.error("Error fetching profile:", error);
          }
        };
        fetchProfile();
      }
    }
  }, [user, profileFromAuth, authLoading]);
  
  const isAdmin = hasModuleAdminAccess("canAccessTriage");
  const isPatient = user?.role === "patient";
  const isCaretaker = user?.role === "caretaker";
  const isProviderOrPractitionerManager = user?.role === 'manager' && (user?.managerScope === 'provider' || user?.managerScope === 'provider');
  const isProviderOrPractitioner =
    user?.role?.toLowerCase() === "provider" ||
    user?.role?.toLowerCase() === "practitioner";
  const [activeTab, setActiveTab] = useState("received");
  const [receivedData, setReceivedData] = useState<any[]>([]);
  const [resolvedData, setResolvedData] = useState<any[]>([]);
  const [isDismissDialogOpen, setIsDismissDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [dismissalReason, setDismissalReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSnoozeClearDialogOpen, setIsSnoozeClearDialogOpen] = useState(false);
  const [selectedAlertForSnooze, setSelectedAlertForSnooze] = useState<any>(null);
  const [pendingFilters, setPendingFilters] = useState<Record<string, any>>({
    patientType: "all",
  });
  const [resolvedFilters, setResolvedFilters] = useState<Record<string, any>>({
    patientType: "all",
  });
  const [facilities, setFacilities] = useState<any[]>([]);
  // const [isInternalNoteDialogOpen, setIsInternalNoteDialogOpen] = useState(false);

  useGetData(
    "/facilities",
    { limit: 500 },
    false,
    !!user,
    (err, data) => {
      if (!err && data?.data?.facilities) {
        setFacilities(data.data.facilities);
      } else {
        setFacilities([]);
      }
    },
  );

  useEffect(() => {
    const pruneFacilityIds = (prev: Record<string, any>) => {
      if (!Array.isArray(prev?.facilityIds) || prev.facilityIds.length === 0) {
        return prev;
      }
      if (!facilities?.length) return prev;
      const validIds = new Set(facilities.map((f: any) => f?._id));
      const nextIds = prev.facilityIds.filter((id: string) => validIds.has(id));
      if (nextIds.length === prev.facilityIds.length) return prev;
      return { ...prev, facilityIds: nextIds };
    };

    setPendingFilters((prev) => pruneFacilityIds(prev));
    setResolvedFilters((prev) => pruneFacilityIds(prev));
  }, [facilities]);

  const triageFilterOptions = useMemo(
    () => [
      {
        label: "Patient Type",
        key: "patientType",
        isMulti: false,
        values: [
          { label: "All", key: "all" },
          { label: "Outpatient", key: "outpatient" },
          { label: "Facility", key: "facility" },
        ],
      },
      {
        label: "Facility Name",
        key: "facilityIds",
        isMulti: true,
        values: facilities.map((facility: any) => ({
          label: facility?.name || "Unnamed facility",
          key: facility?._id,
        })),
      },
    ],
    [facilities],
  );

  const getFacilityName = (facilityRef: any, patientRef?: any) => {
    if (typeof facilityRef === "object" && facilityRef?.name) {
      return facilityRef.name;
    }
    const patientFacility = patientRef?.facilityId;
    if (typeof patientFacility === "object" && patientFacility?.name) {
      return patientFacility.name;
    }
    if (typeof patientFacility === "string" && patientFacility) {
      return patientFacility;
    }
    return "";
  };

  const getPendingAlertTimezone = (alert: any) => {
    if (!alert) return "—";
    const direct =
      alert.resolvedTimezone ?? alert.timezone ?? alert.timeZone;
    if (direct != null && String(direct).trim() !== "") {
      return formatOrganizationTimezoneDisplay(String(direct).trim());
    }
    const patientType = alert?.patientId?.typeOfPatient;
    if (patientType === "outpatient") {
      const iana =
        alert?.organizationId?.timezone ?? alert?.organizationId?.timeZone;
      return formatOrganizationTimezoneDisplay(iana);
    }
    if (patientType === "facility") {
      const iana = alert?.facilityId?.timezone ?? alert?.facilityId?.timeZone;
      return formatOrganizationTimezoneDisplay(iana);
    }
    return getPatientContextTimezoneDisplay(alert?.patientId);
  };

  /** Table passes raw API rows to actions, not viewData — compute tz for navigation */
  const getTimezoneForViewNavigation = (row: any) => {
    if (!row) return "";
    const direct =
      row.resolvedTimezone ?? row.timezone ?? row.timeZone;
    if (direct != null && String(direct).trim() !== "") {
      return formatOrganizationTimezoneDisplay(String(direct).trim());
    }
    if (row.patient && typeof row.patient === "object") {
      const tz = getPatientContextTimezoneDisplay(row.patient);
      if (tz && tz !== "—") return tz;
    }
    const cr = row.latestCriticalReading;
    if (cr && typeof cr === "object") {
      const tz = getPendingAlertTimezone(cr);
      if (tz && tz !== "—") return tz;
    }
    return getPendingAlertTimezone(row);
  };

  // Columns to hide for patient users
  const patientHiddenColumnIds = ["patientId", "name", "phone", "email", "organization"];

  // Column headers for Received tab
  const receivedHeadersAll: ColumnHeader[] = [
    { id: "patientId", label: "Patient ID", isSortable: false },
    { id: "name", label: "Name", isSortable: false },
    { id: "phone", label: "Phone", isSortable: false },
    { id: "email", label: "Email", isSortable: false },
    { id: "organization", label: "Organization", isSortable: false },
    { id: "timezone", label: "Timezone", isSortable: false },
    { id: "caretakerDevice", label: "Device", isSortable: false },
    { id: "reading", label: "Reading", isSortable: false },
    { id: "createdAt", label: "Received At", isSortable: true },
    { id: "status", label: "Status", isSortable: false },
    { id: "facility", label: "Facility Name", isSortable: false },
  ];
  const receivedHeaders = isPatient
    ? receivedHeadersAll.filter((h) => !patientHiddenColumnIds.includes(h.id))
    : receivedHeadersAll;

  // Column headers for Resolved tab
  const resolvedHeadersAll: ColumnHeader[] = [
    { id: "patientId", label: "Patient ID", isSortable: false },
    { id: "name", label: "Name", isSortable: false },
    { id: "phone", label: "Phone", isSortable: false },
    { id: "email", label: "Email", isSortable: false },
    { id: "organization", label: "Organization", isSortable: false },
    { id: "timezone", label: "Timezone", isSortable: false },
    { id: "caretaker", label: "Caretaker", isSortable: false },
    { id: "lastTriage", label: "Last Triage", isSortable: false },
    { id: "timestamp", label: "Triage Time", isSortable: true },
    { id: "resolvedByName", label: "Resolved By", isSortable: false },
    { id: "device", label: "Device", isSortable: false },
    { id: "totalTriage", label: "Total Triage", isSortable: false },
    { id: "facility", label: "Facility Name", isSortable: false },
  ];
  const resolvedHeaders = isPatient
    ? resolvedHeadersAll.filter((h) => !patientHiddenColumnIds.includes(h.id))
    : resolvedHeadersAll;

  const handleRowClick = (data: any) => {
    // Handle row click if needed
    console.log("Row clicked:", data);
  };

  // Check if caretaker is assigned to the patient in the alert
  // Uses profile._id from /api/auth/me response to match against assignedCaretakerIds
  const isCaretakerAssignedToPatient = (alert: any) => {
    if (!isCaretaker) return true; // Not a caretaker, so always allow
    
    const assignedCaretakerIds = alert?.patientId?.assignedCaretakerIds || [];
    if (!Array.isArray(assignedCaretakerIds) || assignedCaretakerIds.length === 0) {
      return false; // No assigned caretakers, disable for caretaker
    }
    
    // Check if profile._id (from /api/auth/me response) exists in assignedCaretakerIds
    // assignedCaretakerIds can be array of strings (IDs) or objects with _id property
    const profileId = profile?._id;
    if (!profileId) return false; // No profile ID available, disable for caretaker
    
    return assignedCaretakerIds.some((caretakerId: any) => {
      return caretakerId === profileId; // profile comes from /api/auth/me response (data.profile)
    });
  };
    
  // const handleAddInternalNote = async (e: React.FormEvent<HTMLFormElement>) => {
  //   e.preventDefault();
  //   const formData = new FormData(e.currentTarget);
  //   const subject = (formData.get("subject") as string)?.trim();
  //   const content = (formData.get("content") as string)?.trim();

  //   if (!content || !subject) {
  //     toast.error("Please enter subject and note content");
  //     return;
  //   }

  //   const { pr, resolveFn, rejectFn } = getPromise();
  //   getPromiseToast(pr);

  //   try {
  //     setBtnLoading(true);
  //       // ADD MODE
  //       const payload = {
  //         content,
  //         subject,
  //         patientId: selectedAlertForSnooze?.patientId?._id as string,
  //         authorName:
  //           profileFromAuth?.name ||
  //           [profileFromAuth?.firstName, profileFromAuth?.lastName].filter(Boolean).join(" ") ||
  //           user?.name ||
  //           [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
  //           "",
  //         createdAt: Date.now(),
  //       };

  //       const { data } = await AxiosInstance().post(
  //         "/patients/internal-note",
  //         payload,
  //       );
  //       // // console.log({ data });
  //       // if(data?.data === null) { 
  //       //   refetchInternalNotes();
  //       // }else{
  //       // setInternalNotes((prev) => [data?.data, ...prev]);}
  //       resolveFn("Internal note added successfully");
      

  //     setIsInternalNoteDialogOpen(false);
  //   } catch (error: any) {
  //     rejectFn(error);
  //     toast.error(error?.response?.data?.message || "Operation failed");
  //   } finally {
  //     setBtnLoading(false);
  //     setSelectedAlertForSnooze(null);
  //   }
  // };
 
  const handleSnoozeClearTriage = (alert: any) => {
    setSelectedAlertForSnooze(alert);
    setIsSnoozeClearDialogOpen(true);
  };

  const handleSnoozeClearConfirm = async (data: { timePeriod?: string; reason?: string; action: "snooze" | "clear" | "false_alarm" }) => {
   
    if (!selectedAlertForSnooze?.alertId) {
      toast.error("Alert ID is missing");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);
      
      if (data.action === "snooze") {
        // Convert time period to minutes
        const timePeriodMap: Record<string, number> = {
          "15 minutes": 15,
          "30 minutes": 30,
          "1 hour": 60,
        };
        const snoozeMinutes = timePeriodMap[data.timePeriod || ""];
        
        if (!snoozeMinutes) {
          toast.error("Invalid time period selected");
          return;
        }
        
        // Calculate snooze time by adding selected minutes to current time
        const currentTime = new Date();
        const snoozeTime = new Date(currentTime.getTime() + snoozeMinutes * 60 * 1000);
        const snoozeTimeISO = snoozeTime.toISOString();
        
        await AxiosInstance().patch(`/critical-readings/${selectedAlertForSnooze.alertId}/snooze`, {
          snoozeTime: snoozeTimeISO,
        });
        resolveFn("Alert snoozed successfully");
      } else if (data.action === "clear") {
        // Call resolve API with the selected reason as resolutionNotes
        await AxiosInstance().patch(`/critical-readings/${selectedAlertForSnooze.alertId}/resolve`, {
          resolutionNotes: data.reason,
        });
        resolveFn("Triage cleared successfully");
        // setIsInternalNoteDialogOpen(true);
      } else if (data.action === "false_alarm") {
        // Call dismiss API with the selected reason as dismissalReason
        if (!data.reason?.trim()) {
          toast.error("Please provide a dismissal reason");
          return;
        }
        await AxiosInstance().patch(`/critical-readings/${selectedAlertForSnooze.alertId}/dismiss`, {
          dismissalReason: data.reason.trim(),
        });
        resolveFn("Alert dismissed as false alarm successfully");
        
      }

      setIsSnoozeClearDialogOpen(false);
      // if(data.action !== "clear") {
        setSelectedAlertForSnooze(null);
      // }
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      console.error("Error:", error);
      rejectFn(error?.response?.data?.message || "Failed to process request");
    } finally {
      setBtnLoading(false);
    }
  };

  const handleDismiss = (alert: any) => {
    setSelectedAlert(alert);
    setIsDismissDialogOpen(true);
    setDismissalReason("");
  };

  const handleDismissConfirm = async () => {
    if (!selectedAlert?.alertId) {
      toast.error("Alert ID is missing");
      return;
    }

    if (!dismissalReason.trim()) {
      toast.error("Please provide a dismissal reason");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);
      await AxiosInstance().patch(`/critical-readings/${selectedAlert.alertId}/dismiss`, {
        dismissalReason: dismissalReason.trim(),
      });

      resolveFn("Alert dismissed successfully");
      setIsDismissDialogOpen(false);
      setSelectedAlert(null);
      setDismissalReason("");
      // Refresh the data
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      rejectFn(error);
      toast.error(
        error?.response?.data?.message || "Failed to dismiss alert"
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const handleResolve = (alert: any) => {
    // console.log("alert -> ",{alert});
    navigate("/alerts/resolve", { state: { alert } });
  };

  const handleView = (alert: any) => {
    const timezone = getTimezoneForViewNavigation(alert);
    navigate("/alerts/view", {
      state: { alert: { ...alert, timezone } },
    });
  };


  const handleFeedback = (alert: any) => {
    setSelectedAlert(alert);
    setIsFeedbackDialogOpen(true);
    setFeedback("");
  };

  const handleFeedbackConfirm = async () => {
    if (!selectedAlert?.alertId) {
      toast.error("Alert ID is missing");
      return;
    }

    if (!feedback.trim()) {
      toast.error("Please provide feedback");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);
      await AxiosInstance().patch(`/critical-readings/${selectedAlert.alertId}/feedback`, {
        feedback: feedback.trim(),
      });

      resolveFn("Feedback submitted successfully");
      setIsFeedbackDialogOpen(false);
      setSelectedAlert(null);
      setFeedback("");
      // Refresh the data
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      rejectFn(error);
      toast.error(
        error?.response?.data?.message || "Failed to submit feedback"
      );
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <DashboardLayout activePage="/alerts">
      <div>
        {/* <Card> */}
          {/* <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Alerts
            </CardTitle>
          </CardHeader> */}
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="h-10 flex w-full md:w-fit rounded-lg bg-grey-light border border-grey-light p-0 overflow-hidden">
                <TabsTrigger
                  value="received"
                  className="teachers text-sm px-4 flex-1 md:flex-none data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none rounded-none"
                >
                  Pending
                </TabsTrigger>
                <TabsTrigger
                  value="resolved"
                  className="teachers text-sm px-4 flex-1 md:flex-none data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none rounded-none"
                >
                    Resolved
                </TabsTrigger>
              </TabsList>

              <TabsContent value="received" className="mt-4">
                <TableWithSearch
                  heading="Received Alerts"
                  url="/critical-readings"
                  universalLoadingSkeleton
                  params={{
                    status: "pending",
                    refreshKey,
                    ...(!isAllSelected && selectedOrgId
                      ? { organizationId: selectedOrgId }
                      : {}),
                  }}
                  apiDataKey="alerts"
                  fetchCondition={activeTab === "received"}
                  isIncludeSearch={true}
                  searchPlaceholder="Search patient name"
                  columnHeaders={receivedHeaders}
                  data={receivedData}
                  viewData={receivedData.map((alert: any) => {
                    // Format readingValues to show all key-value pairs
                    const readingValues = alert?.readingValues || {};
                    const readingValuesFormatted = Object.entries(readingValues)
                      .map(([key, value]) => {
                        // Capitalize first letter and replace underscores with spaces
                        const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
                        return `${formattedKey}: ${value}`;
                      })
                      .join(", ");
                    
                    // Check if snoozeTime is in the future
                    const isSnoozed = alert?.snoozeTime && new Date(alert.snoozeTime) > new Date();
                    const patientIdValue = alert?.patientId?.patientId || alert?.patientId?._id || "";
                    const patientName = `${alert?.patientId?.firstName || ""} ${alert?.patientId?.middleName || ""} ${alert?.patientId?.lastName || ""}`.trim().replace(/\s+/g, " ");
                    const isCritical = alert?.hasPendingCriticalReading ;
                    
                    return {
                      ...alert,
                      _id: alert?.patientId?._id || alert?._id, // Preserve patient _id for navigation
                      patientId: isSnoozed ? (
                        <span className="flex items-center gap-2">
                          {patientIdValue}
                          <Clock className="w-4 h-4 text-primary" />
                        </span>
                      ) : patientIdValue,
                      name: isCritical ? (
                        <span className="text-red-500">{patientName}</span>
                      ) : patientName,
                      phone: alert?.patientId?.primaryNumber || "",
                      email: alert?.patientId?.email || "",
                      organization: (() => {
                        const orgName = alert?.organizationId?.organizationName || alert?.organizationId || "";
                        return typeof orgName === 'string' && orgName
                          ? orgName
                              .split(' ')
                              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                              .join(' ')
                          : orgName;
                      })(),
                      timezone: getPendingAlertTimezone(alert),
                      caretakerDevice: alert?.deviceType?.replace(/_/g, " ") || "",
                      reading: readingValuesFormatted || alert?.readingId?.readingId || alert?.readingId?._id || "",
                      createdAt: alert?.createdAtLocal ? formatDateTime12(alert?.createdAtLocal) : alert?.createdAt ? formatDateTime12(alert.createdAt) : (alert?.readingId?.readingDate ? formatDateTime12(alert.readingId.readingDate) : ""),
                      status: alert?.status?.replace(/_/g, " ") || "",
                      facility: getFacilityName(alert?.facilityId, alert?.patientId),
                    };
                  })}
                  setData={setReceivedData}
                  isActionColumn={false}
                  filtersOptions={triageFilterOptions}
                  handleAction={() => {}}
                  filters={pendingFilters}
                  setFilters={setPendingFilters}
                  clickRow={handleRowClick}
                  isColumnSelection
                  columnPreferenceKey="alerts-pending"
                  persistFilterKeys={["facilityIds"]}
                  isDownloadData={isAdmin}
                  isSelectable={false}
                  selectActions={[]}
                  donotFetch={false}
                  isIgnoreSearchParams={true}
                  moreActions={
                    isPatient || isProviderOrPractitioner || isProviderOrPractitionerManager
                      ? []
                      : [
                          {
                            label: "Snooze/Clear Triage",
                            icon: <Clock className="h-4 w-4" />,
                            onClick: handleSnoozeClearTriage,
                            isShow: (alert: any) => alert?.status === "pending",
                            isDisabled: (alert: any) => {
                              // Check if snoozed
                              const isSnoozed = alert?.snoozeTime && new Date(alert.snoozeTime) > new Date();

                              // For caretakers, also check if they are assigned to the patient
                              if (isCaretaker) {
                                return isSnoozed || !isCaretakerAssignedToPatient(alert);
                              }
                              return isSnoozed;
                            },
                          },
                          {
                            label: "Resolve",
                            icon: <CheckCircle className="h-4 w-4" />,
                            onClick: handleResolve,
                            isShow: (alert: any) => alert?.status === "pending",
                            isDisabled: (alert: any) => {
                              // Check if snoozed
                              const isSnoozed = alert?.snoozeTime && new Date(alert.snoozeTime) > new Date();
                              // For caretakers, also check if they are assigned to the patient
                              if (isCaretaker) {
                                return isSnoozed || !isCaretakerAssignedToPatient(alert);
                              }
                              return isSnoozed;
                            },
                          },
                        ]
                  }
                />
              </TabsContent>

              <TabsContent value="resolved" className="mt-4">
                {activeTab === "resolved" && user?.role === "patient" ? 
                (() => {
                  navigate("/alerts/view");
                  return null;
                })()
                : <TableWithSearch
                  heading="Resolved Alerts"
                  url="/critical-readings/patients"
                  universalLoadingSkeleton
                  params={{
                    status: "resolved,invalid",
                    limit: 20,
                    ...(!isAllSelected && selectedOrgId
                      ? { organizationId: selectedOrgId }
                      : {}),
                  }}
                  apiDataKey="patients"
                  fetchCondition={activeTab === "resolved"}
                  isIncludeSearch={true}
                  searchPlaceholder="Search patient name or ID..."
                  columnHeaders={resolvedHeaders}
                  data={resolvedData}
                  viewData={resolvedData.map((item: any) => {
                    const patient = item?.patient || {};
                    const criticalReading = item?.latestCriticalReading || {};
                    const readingId = criticalReading?.readingId || {};
                    // console.log("criticalReading -> ",{criticalReading});
                    // Format readingValues to show all key-value pairs
                    const readingValues = criticalReading?.readingValues || {};
                    const readingValuesFormatted = Object.entries(readingValues)
                      .map(([key, value]) => {
                        // Capitalize first letter and replace underscores with spaces
                        const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
                        return `${formattedKey}: ${value}`;
                      })
                      .join(", ");
                    
                    // Extract caretaker names from assignedCaretakerIds array
                    const caretakerNames = Array.isArray(patient?.assignedCaretakerIds)
                      ? patient.assignedCaretakerIds
                          .map((caretaker: any) => {
                            if (typeof caretaker === 'string') return caretaker;
                            return `${caretaker?.firstName || ""} ${caretaker?.middleName || ""} ${caretaker?.lastName || ""}`.trim();
                          })
                          .filter(Boolean)
                          .join(", ")
                      : "";
                    
                    // Extract triage time - check multiple possible fields
                    const triageTime = criticalReading?.triageTime 
                      || criticalReading?.lastTriageAt 
                      || criticalReading?.resolvedAt 
                      || criticalReading?.updatedAt 
                      || criticalReading?.createdAt 
                      || "";
                    
                    // Extract last triage - same as triageTime or use resolvedAt
                    const lastTriage = criticalReading?.lastTriageAt 
                      || criticalReading?.resolvedAt 
                      || criticalReading?.triageTime 
                      || criticalReading?.updatedAt 
                      || "";

                    const createdAtLocal = criticalReading?.createdAtLocal || "";
                    // console.log("createdAtLocal -> ",{createdAtLocal});
                    return {
                      ...item,
                      _id: patient?._id || item?._id, // Preserve patient _id for navigation
                      patientId: patient?.patientId || patient?._id || "",
                      name: `${patient?.firstName || ""} ${patient?.middleName || ""} ${patient?.lastName || ""}`.trim().replace(/\s+/g, " "),
                      phone: patient?.primaryNumber || "",
                      email: patient?.email || "",
                      organization: (() => {
                        const orgName = patient?.organizationId?.organizationName || criticalReading?.organizationId?.organizationName || "";
                        return typeof orgName === 'string' && orgName
                          ? orgName
                              .split(' ')
                              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                              .join(' ')
                          : orgName;
                      })(),
                      timezone: getPatientContextTimezoneDisplay(patient),
                      caretaker: caretakerNames,
                      lastTriage: lastTriage ? formatDate(lastTriage) : "",
                      timestamp:  createdAtLocal ? createdAtLocal : triageTime ? formatDateTime12(triageTime) : "",
                      resolvedByName:
                        criticalReading?.resolvedBy?.name ||
                        item?.resolvedBy?.name ||
                        criticalReading?.resolvedByName ||
                        (typeof criticalReading?.resolvedBy === "string"
                          ? criticalReading.resolvedBy
                          : "") ||
                        (typeof item?.resolvedBy === "string"
                          ? item.resolvedBy
                          : "") ||
                        "",
                      device: criticalReading?.deviceType
                        ? criticalReading.deviceType
                            .replace(/_/g, " ")
                            .split(" ")
                            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(" ")
                        : "",
                      totalTriage: item?.criticalReadingCount || 0,
                      reading: readingValuesFormatted || readingId?.readingId || readingId?._id || "",
                      facility: getFacilityName(patient?.facilityId, patient),
                    };
                  })}
                  setData={setResolvedData}
                  isActionColumn={false}
                  filtersOptions={triageFilterOptions}
                  handleAction={() => {}}
                  filters={resolvedFilters}
                  setFilters={setResolvedFilters}
                  clickRow={handleRowClick}
                  isColumnSelection
                  columnPreferenceKey="alerts-resolved"
                  persistFilterKeys={["facilityIds"]}
                  isDownloadData={isAdmin}
                  isSelectable={false}
                  selectActions={[]}
                  donotFetch={false}
                  isIgnoreSearchParams={true}
                  moreActions={[
                    {
                      label: "View",
                      icon: <CheckCircle className="h-4 w-4" />,
                      onClick: handleView,
                      isShow: (alert: any) => true,
                    },
                  ]}
                /> }
              </TabsContent>
            </Tabs>
          </CardContent>
        {/* </Card> */}

        {/* Caretaker Feedback Dialog */}
        <Dialog open={isFeedbackDialogOpen} onOpenChange={setIsFeedbackDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Caretaker Feedback</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="feedback">
                  Feedback <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="feedback"
                  placeholder="Enter caretaker feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsFeedbackDialogOpen(false);
                    setSelectedAlert(null);
                    setFeedback("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={btnLoading || !feedback.trim()}
                  onClick={handleFeedbackConfirm}
                >
                  {btnLoading ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Snooze/Clear Triage Dialog */}
        <SnoozeClearTriageDialog
          isOpen={isSnoozeClearDialogOpen}
          onClose={() => {
            setIsSnoozeClearDialogOpen(false);
            setSelectedAlertForSnooze(null);
          }}
          onConfirm={handleSnoozeClearConfirm}
          isLoading={btnLoading}
        />
      </div>

      {/* Internal Note Dialog */}

      {/* <Dialog
          open={isInternalNoteDialogOpen}
          onOpenChange={setIsInternalNoteDialogOpen}
        >
          <DialogContent 
            className="w-[50vw] !max-w-none max-h-[50vh] overflow-x-hidden"
          >
            <DialogHeader>
              <DialogTitle>
                Add Internal Note
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleAddInternalNote}
            >
              

              <div className="space-y-2 pr-1 mb-4">

                <Label htmlFor="subject-content">Subject</Label>
                <Textarea
                  id="subject-content"
                  name="subject"
                  placeholder="Enter Subject..."
                  rows={1}
                  defaultValue={
                    ""
                  }
                  maxLength={50}
                  required
                /> 


                <Label htmlFor="internal-note-content">Note Content</Label>
                <Textarea
                  id="internal-note-content"
                  name="content"
                  placeholder="Enter internal note..."
                  rows={6}
                  defaultValue={
                    ""
                  }
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsInternalNoteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button disabled={btnLoading} type="submit">
                  {"Add Note"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog> */}
    </DashboardLayout>
  );
};

export default Alerts;





